package diana.soleil.design.iraniancanadienbazzar.feature.gems

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.common.Formatters
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.Recipe
import diana.soleil.design.iraniancanadienbazzar.core.model.RecipeCategory
import diana.soleil.design.iraniancanadienbazzar.core.model.RecipeDifficulty
import diana.soleil.design.iraniancanadienbazzar.core.model.RecipeRegion
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn

/** How the recipe grid is ordered. */
enum class RecipeSort { MostLiked, Newest, Quickest }

/**
 * Recipe browser filters — pure in-memory predicates over the streamed corpus
 * (`docs/HIDDEN_GEMS_PLAN.md` §5). [maxTotalMinutes] caps prep+cook; null = any.
 */
data class RecipeFilters(
    val region: RecipeRegion? = null,
    val category: RecipeCategory? = null,
    val difficulty: RecipeDifficulty? = null,
    val maxTotalMinutes: Int? = null,
    val sort: RecipeSort = RecipeSort.Newest,
)

data class RecipesUiState(
    val loading: Boolean = true,
    val error: Boolean = false,
    val recipes: List<Recipe> = emptyList(),
    val filters: RecipeFilters = RecipeFilters(),
    val query: String = "",
    /** Regions that actually have published recipes — the chip rail only renders these. */
    val availableRegions: List<RecipeRegion> = emptyList(),
)

/**
 * Recipe browser (`/gems/recipes`): streams the published corpus and applies the region-hero
 * filter rail + secondary pills + trilingual search client-side (SearchViewModel pattern).
 */
class RecipesViewModel(
    gemsRepository: GemsRepository,
) : ViewModel() {

    private val filtersFlow = MutableStateFlow(RecipeFilters())
    private val queryFlow = MutableStateFlow("")

    val state: StateFlow<RecipesUiState> =
        combine(
            gemsRepository.observeRecipes(),
            filtersFlow,
            queryFlow,
        ) { result, filters, query ->
            when (result) {
                is DataResult.Success -> RecipesUiState(
                    loading = false,
                    recipes = result.data.applyRecipeFilters(filters, query),
                    filters = filters,
                    query = query,
                    availableRegions = RecipeRegion.entries.filter { region ->
                        result.data.any { it.region == region }
                    },
                )
                is DataResult.Failure -> RecipesUiState(loading = false, error = true, filters = filters, query = query)
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), RecipesUiState())

    fun onQueryChange(query: String) {
        queryFlow.value = query
    }

    fun onFiltersChange(filters: RecipeFilters) {
        filtersFlow.value = filters
    }
}

private fun List<Recipe>.applyRecipeFilters(filters: RecipeFilters, query: String): List<Recipe> {
    val normalized = Formatters.normalizePersian(query).trim().lowercase()
    val tokens = normalized.split(' ').filter { it.isNotBlank() }
    return asSequence()
        .filter { recipe ->
            (filters.region == null || recipe.region == filters.region) &&
                (filters.category == null || recipe.category == filters.category) &&
                (filters.difficulty == null || recipe.difficulty == filters.difficulty) &&
                (filters.maxTotalMinutes == null || recipe.totalMinutes <= filters.maxTotalMinutes) &&
                (tokens.isEmpty() || tokens.all { token -> recipe.searchHaystack().contains(token) })
        }
        .sortedWith(
            when (filters.sort) {
                RecipeSort.MostLiked ->
                    compareByDescending<Recipe> { it.likeCount }.thenByDescending { it.publishedAtMillis }
                RecipeSort.Newest -> compareByDescending { it.publishedAtMillis }
                RecipeSort.Quickest ->
                    compareBy<Recipe> { it.totalMinutes }.thenByDescending { it.publishedAtMillis }
            },
        )
        .toList()
}

/** Lowercased, Persian-normalized bag of every localized variant + tags (SearchViewModel pattern). */
private fun Recipe.searchHaystack(): String =
    Formatters.normalizePersian(
        buildList {
            addAll(listOfNotNull(title.en, title.fr, title.fa))
            addAll(listOfNotNull(summary.en, summary.fr, summary.fa))
            add(region.wire)
            add(category.wire)
            addAll(tags)
            ingredients.forEach { addAll(listOfNotNull(it.name.en, it.name.fr, it.name.fa)) }
        }.joinToString(" "),
    ).lowercase()
